import classNames from 'classnames';

import React, {
  forwardRef,
  ForwardRefExoticComponent,
  HTMLAttributes,
  RefAttributes,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Counter } from '../counter';
import { useAssetUrl } from '../lib/hooks';
import { PLACEHOLDER } from '../lib/placeholder';

import { COUNTRY_CODES } from './lib/country-codes';
import styles from './image.scss';

export type Props = RefAttributes<HTMLDivElement> &
  HTMLAttributes<HTMLDivElement> & {
    width?: string;
    height?: string;
    src?: string;
    name?: string;
    placeholder?: string;
    alt?: string;
    count?: number;
    dataTestId?: string;
  };

const COMPONENT_ID = 'modulor-image';

const NAME_PATTERN = /^[a-zA-Z0-9_-]+$/;

export const Image: ForwardRefExoticComponent<Props> = forwardRef<HTMLDivElement, Props>(
  (
    {
      width,
      height,
      src,
      name,
      placeholder,
      alt,
      count,
      onClick,
      style,
      dataTestId = COMPONENT_ID,
      children,
      className,
      ...rest
    },
    ref,
  ) => {
    const container = useRef<HTMLDivElement>(null);

    const [useRemotePlaceholder, setUseRemotePlaceholder] = useState(false);
    const [useLocalPlaceholder, setUseLocalPlaceholder] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const imageName = useMemo(() => COUNTRY_CODES[name] ?? name, [name]);

    const imageUrlXhdpi = useAssetUrl(src || imageName, 'xhdpi');
    const imageUrlMdpi = useAssetUrl(src || imageName, 'mdpi');
    const placeholderUrlXhdpi = useAssetUrl(placeholder, 'xhdpi');
    const placeholderUrlMdpi = useAssetUrl(placeholder, 'mdpi');

    useImperativeHandle(ref, () => container.current);

    useEffect(() => {
      if (!src) return;
      setIsLoading(true);
    }, [src]);

    useEffect(() => {
      setUseLocalPlaceholder(false);
      setUseRemotePlaceholder(false);
    }, [imageUrlMdpi, imageUrlXhdpi, placeholderUrlMdpi, placeholderUrlXhdpi]);

    const imageStyle = {
      width: width ? `${width}px` : 'auto',
      height: height ? `${height}px` : 'auto',
    };

    if (useLocalPlaceholder) {
      return (
        <div
          style={{ ...imageStyle, ...style }}
          className={styles.placeholder}
          dangerouslySetInnerHTML={{
            __html: PLACEHOLDER,
          }}
        />
      );
    }

    const getImageUrls = () => ({
      xhdpi: imageUrlXhdpi,
      mdpi: imageUrlMdpi,
    });

    const getPlaceholderUrls = () => {
      const isName = NAME_PATTERN.test(placeholder || '');

      if (isName) {
        return {
          xhdpi: placeholderUrlXhdpi,
          mdpi: placeholderUrlMdpi,
        };
      }

      return { src: placeholder };
    };

    const onLoad = () => {
      setIsLoading(false);
      if (container.current) container.current.style.display = 'inline-block';
    };

    const onError = () => {
      if (!useRemotePlaceholder && placeholder) {
        setUseRemotePlaceholder(true);
      } else {
        setUseLocalPlaceholder(true);
        setUseRemotePlaceholder(false);
      }
    };

    const image = getImageUrls();
    const actualPlaceholder = getPlaceholderUrls();

    const actualSrc = useRemotePlaceholder ? actualPlaceholder.src : src;
    const xhdpi = useRemotePlaceholder ? actualPlaceholder.xhdpi : image.xhdpi;
    const mdpi = useRemotePlaceholder ? actualPlaceholder.mdpi : image.mdpi;

    const classes = classNames(styles.host, className, {
      [styles.clickable]: onClick != null,
    });

    return (
      <div
        data-testid={dataTestId}
        data-component-name={COMPONENT_ID}
        data-id={dataTestId}
        ref={container}
        className={classes}
        style={style}
        {...rest}>
        <div className={styles.container} style={imageStyle}>
          <picture data-testid={`${dataTestId}-picture`}>
            <source srcSet={actualSrc} />
            <source srcSet={xhdpi} media="(min-resolution: 2dppx)" />
            <img
              style={{ display: isLoading ? 'none' : 'inline-block' }}
              className={styles.img}
              src={mdpi}
              onLoad={onLoad}
              onError={onError}
              onClick={onClick}
              alt={alt || ''}
            />
          </picture>
          <span className={styles.counter}>
            {children == null ? (
              <Counter value={count} notEmpty dataTestId={`${dataTestId}-counter`} />
            ) : (
              children
            )}
          </span>
        </div>
      </div>
    );
  },
);

Image.displayName = 'Image';
